import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  Dialog,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  Chip,
  SelectChangeEvent
} from '@mui/material';
import { ApiStatus } from '../../utils/api.types';
import { ITCSPaymentForm } from '../../utils/tcsSettlement.types';

interface TCSPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  paymentForm: ITCSPaymentForm;
  onPaymentFormChange: (field: string, value: any) => void;
  onReturnDetailsChange: (field: string, value: any) => void;
  onSubmit: () => void;
  paymentStatus: ApiStatus;
  bankLedgers: any[];
}

const TCSPaymentDialog: React.FC<TCSPaymentDialogProps> = ({
  open,
  onClose,
  paymentForm,
  onPaymentFormChange,
  onReturnDetailsChange,
  onSubmit,
  paymentStatus,
  bankLedgers
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Calculate due date dynamically (7th of next month from payment date)
  const getDueDate = () => {
    const paymentDate = paymentForm.paymentDate ? new Date(paymentForm.paymentDate) : new Date();
    const dueDate = new Date(paymentDate.getFullYear(), paymentDate.getMonth() + 1, 7);
    return dueDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <Box>
        {/* Header */}
        <Grid container>
          <Grid item xs={12} p={2} pl={2} bgcolor='#e9ecf1' style={{ borderTopLeftRadius: 8, borderTopRightRadius: 8, height: 50 }}>
            <div style={{ float: 'right', marginTop: -7, marginRight: -5 }}>
              <Button
                variant="contained"
                onClick={onSubmit}
                disabled={
                  paymentStatus === ApiStatus.LOADING ||
                  !paymentForm.challanNumber ||
                  !paymentForm.bankAccount ||
                  paymentForm.tcsPayable === 0
                }
                style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', marginRight: 10 }}
                size='small'
              >
                {paymentStatus === ApiStatus.LOADING ? <CircularProgress size={20} /> : 'Record Payment'}
              </Button>
              <Button
                style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', marginRight: 10 }}
                variant="contained"
                size='small'
                onClick={onClose}
              >
                Cancel
              </Button>
            </div>
            <h3 style={{ marginTop: -2 }}>Record TCS Payment to Government</h3>
          </Grid>
        </Grid>

        {/* Form Content */}
        <Grid container p={2} spacing={2}>
          {/* TCS Return Details */}
          <Grid item xs={12} mt={1}>
            <Divider style={{ marginRight: -8 }}>
              <Chip label="TCS Return Details (Form 27EQ)" size="small" />
            </Divider>
          </Grid>

          <Grid item xs={4} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>TCS Section*</h5>
            <Select
              className='c-text-field-4'
              value={paymentForm.returnDetails.section}
              onChange={(e: SelectChangeEvent<any>) => onReturnDetailsChange('section', e.target.value)}
              disableUnderline
              required
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1.5px solid #e9ecf1",
                    boxShadow: 'none',
                    borderRadius: '5px'
                  }
                }
              }}
              variant='outlined'
              sx={{
                width: '100%',
                boxShadow: "none",
                paddingTop: '0px !important',
                borderRadius: '7px !important',
                marginTop: '4px',
                '& .MuiOutlinedInput-input': { p: 0.7 },
                marginBottom: '3px',
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
              }}
            >
              <MenuItem value="206C">206C - Sale of Goods</MenuItem>
              <MenuItem value="206CA">206CA - Sale of Motor Vehicle</MenuItem>
              <MenuItem value="206CB">206CB - Tendu Leaves</MenuItem>
              <MenuItem value="206CC">206CC - Forest Produce</MenuItem>
              <MenuItem value="206CCA">206CCA - Minerals</MenuItem>
              <MenuItem value="OTHERS">Others</MenuItem>
            </Select>
          </Grid>

          <Grid item xs={4}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Form Type*</h5>
            <Select
              className='c-text-field-4'
              value={paymentForm.returnDetails.formType}
              onChange={(e: SelectChangeEvent<any>) => onReturnDetailsChange('formType', e.target.value)}
              disableUnderline
              required
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1.5px solid #e9ecf1",
                    boxShadow: 'none',
                    borderRadius: '5px'
                  }
                }
              }}
              variant='outlined'
              sx={{
                width: '100%',
                boxShadow: "none",
                paddingTop: '0px !important',
                borderRadius: '7px !important',
                marginTop: '4px',
                '& .MuiOutlinedInput-input': { p: 0.7 },
                marginBottom: '3px',
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
              }}
            >
              <MenuItem value="27EQ">Form 27EQ (Quarterly)</MenuItem>
            </Select>
          </Grid>

          <Grid item xs={4} pl={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Collector Type*</h5>
            <Select
              className='c-text-field-4'
              value={paymentForm.returnDetails.collectorType}
              onChange={(e: SelectChangeEvent<any>) => onReturnDetailsChange('collectorType', e.target.value)}
              disableUnderline
              required
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1.5px solid #e9ecf1",
                    boxShadow: 'none',
                    borderRadius: '5px'
                  }
                }
              }}
              variant='outlined'
              sx={{
                width: '100%',
                boxShadow: "none",
                paddingTop: '0px !important',
                borderRadius: '7px !important',
                marginTop: '4px',
                '& .MuiOutlinedInput-input': { p: 0.7 },
                marginBottom: '3px',
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
              }}
            >
              <MenuItem value="COMPANY">Company</MenuItem>
              <MenuItem value="NON_COMPANY">Non-Company</MenuItem>
            </Select>
          </Grid>

          {/* Payment Details */}
          <Grid item xs={12} mt={1}>
            <Divider style={{ marginRight: -8 }}>
              <Chip label="Payment Details" size="small" />
            </Divider>
          </Grid>

          <Grid item xs={6} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>TCS Payable Amount*</h5>
            <TextField
              placeholder='TCS Payable Amount'
              className='c-text-field-4'
              type="number"
              value={paymentForm.tcsPayable}
              onChange={(e) => onPaymentFormChange('tcsPayable', parseFloat(e.target.value) || 0)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              size='small'
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>

          <Grid item xs={6} pl={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Payment Date*</h5>
            <TextField
              placeholder='Payment Date'
              className='c-text-field-4'
              type="date"
              value={paymentForm.paymentDate}
              onChange={(e) => onPaymentFormChange('paymentDate', e.target.value)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              required
              size='small'
              InputProps={{
                disableUnderline: true
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Challan Details */}
          <Grid item xs={12} mt={1}>
            <Divider style={{ marginRight: -8 }}>
              <Chip label="Challan Details (Required for Compliance)" size="small" />
            </Divider>
          </Grid>

          <Grid item xs={4} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Challan Number (CIN)*</h5>
            <TextField
              placeholder='Enter CIN'
              className='c-text-field-4'
              value={paymentForm.challanNumber}
              onChange={(e) => onPaymentFormChange('challanNumber', e.target.value)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              required
              size='small'
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>

          <Grid item xs={4}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>BSR Code*</h5>
            <TextField
              placeholder='7-digit bank code'
              className='c-text-field-4'
              value={paymentForm.bsrCode}
              onChange={(e) => onPaymentFormChange('bsrCode', e.target.value)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              inputProps={{ maxLength: 7 }}
              required
              size='small'
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>

          <Grid item xs={4} pl={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Assessment Year*</h5>
            <TextField
              placeholder='2025-26'
              className='c-text-field-4'
              value={paymentForm.assessmentYear}
              onChange={(e) => onPaymentFormChange('assessmentYear', e.target.value)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              required
              size='small'
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>

          {/* Bank & Payment Mode */}
          <Grid item xs={6} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Bank Account*</h5>
            <Select
              className='c-text-field-4'
              value={paymentForm.bankAccount}
              onChange={(e: SelectChangeEvent<any>) => onPaymentFormChange('bankAccount', e.target.value)}
              disableUnderline
              required
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1.5px solid #e9ecf1",
                    boxShadow: 'none',
                    borderRadius: '5px',
                    maxHeight: 300
                  }
                }
              }}
              variant='outlined'
              sx={{
                width: '100%',
                boxShadow: "none",
                paddingTop: '0px !important',
                borderRadius: '7px !important',
                marginTop: '4px',
                '& .MuiOutlinedInput-input': { p: 0.7 },
                marginBottom: '3px',
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
              }}
            >
              {bankLedgers.length === 0 ? (
                <MenuItem disabled value="">
                  No bank accounts found
                </MenuItem>
              ) : (
                bankLedgers.map((bank: any) => (
                  <MenuItem key={bank._id} value={bank._id}>
                    {bank.ledgerName || bank.accountName}
                  </MenuItem>
                ))
              )}
            </Select>
          </Grid>

          <Grid item xs={6} pl={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Payment Mode*</h5>
            <Select
              className='c-text-field-4'
              value={paymentForm.paymentMode}
              onChange={(e: SelectChangeEvent<any>) => onPaymentFormChange('paymentMode', e.target.value)}
              disableUnderline
              required
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1.5px solid #e9ecf1",
                    boxShadow: 'none',
                    borderRadius: '5px'
                  }
                }
              }}
              variant='outlined'
              sx={{
                width: '100%',
                boxShadow: "none",
                paddingTop: '0px !important',
                borderRadius: '7px !important',
                marginTop: '4px',
                '& .MuiOutlinedInput-input': { p: 0.7 },
                marginBottom: '3px',
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
              }}
            >
              <MenuItem value="NEFT">NEFT</MenuItem>
              <MenuItem value="RTGS">RTGS</MenuItem>
              <MenuItem value="UPI">UPI</MenuItem>
              <MenuItem value="NETBANKING">Net Banking</MenuItem>
              <MenuItem value="CHEQUE">Cheque</MenuItem>
            </Select>
          </Grid>

          <Grid item xs={6} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Reference Number (Optional)</h5>
            <TextField
              placeholder='Reference Number'
              className='c-text-field-4'
              value={paymentForm.referenceNumber}
              onChange={(e) => onPaymentFormChange('referenceNumber', e.target.value)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              size='small'
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Remarks (Optional)</h5>
            <TextField
              placeholder='Remarks'
              className='c-text-field-4'
              multiline
              rows={3}
              value={paymentForm.remarks}
              onChange={(e) => onPaymentFormChange('remarks', e.target.value)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              size='small'
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>

          {/* Important Notice */}
          <Grid item xs={12} mt={2}>
            <Box sx={{ bgcolor: '#d1ecf1', border: '1px solid #17a2b8', borderRadius: 1, p: 1.5 }}>
              <Typography variant="body2" fontWeight="600" color="#0c5460">
                ⚠️ Important Compliance Notes:
              </Typography>
              <Typography variant="caption" display="block" color="#0c5460" mt={0.5}>
                • TCS payment due date: {getDueDate()}
              </Typography>
              <Typography variant="caption" display="block" color="#0c5460">
                • Quarterly TCS return (Form 27EQ) due dates: Q1-July 15, Q2-Oct 15, Q3-Jan 15, Q4-May 15
              </Typography>
              <Typography variant="caption" display="block" color="#0c5460">
                • Late deposit attracts 1.5% interest per month
              </Typography>
              <Typography variant="caption" display="block" color="#0c5460">
                • Late filing penalty: ₹200 per day (max ₹1,00,000)
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};

export default TCSPaymentDialog;
